import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CATEGORIES, SECTORS } from "@/data/catalog";
import BusinessCard from "@/components/BusinessCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Search, Filter, ShieldCheck, X } from "lucide-react";
import SectorPills from "@/components/SectorPills";

function FilterPanel({ filters, setFilters }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs uppercase tracking-widest text-emerald-800 font-bold">Category</Label>
        <div className="mt-2 space-y-2">
          <button
            onClick={() => setFilters({ ...filters, category: "" })}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${!filters.category ? "border-emerald-700 bg-emerald-50 text-emerald-900 font-semibold" : "border-slate-200 text-slate-600 hover:border-emerald-400"}`}
          >
            All categories
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilters({ ...filters, category: c.key })}
              data-testid={`filter-cat-${c.key}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm border ${filters.category === c.key ? "border-emerald-700 bg-emerald-50 text-emerald-900 font-semibold" : "border-slate-200 text-slate-600 hover:border-emerald-400"}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-emerald-800 font-bold">Min rating: {filters.minRating}★</Label>
        <Slider value={[filters.minRating]} onValueChange={(v) => setFilters({ ...filters, minRating: v[0] })} max={5} step={0.5} className="mt-3" data-testid="filter-rating-slider" />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="ver" checked={filters.verifiedOnly} onCheckedChange={(c) => setFilters({ ...filters, verifiedOnly: !!c })} data-testid="filter-verified-checkbox" />
        <Label htmlFor="ver" className="cursor-pointer flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Verified only</Label>
      </div>
    </div>
  );
}

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(sp.get("q") || "");
  const [filters, setFilters] = useState({
    category: sp.get("category") || "",
    sector: sp.get("sector") || "",
    minRating: 0,
    verifiedOnly: false,
  });

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filters.category) params.category = filters.category;
      if (filters.sector) params.sector = filters.sector;
      if (filters.minRating > 0) params.min_rating = filters.minRating;
      if (filters.verifiedOnly) params.verified_only = true;
      const { data } = await api.get("/businesses", { params });
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResults(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (filters.category) params.set("category", filters.category);
    if (filters.sector) params.set("sector", filters.sector);
    setSp(params, { replace: true });
    // eslint-disable-next-line
  }, [q, filters.category, filters.sector]);

  const activeCat = CATEGORIES.find((c) => c.key === filters.category);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      <div className="flex flex-col gap-3 mb-5 sm:mb-6 min-w-0">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>
          {activeCat ? activeCat.name : "Explore Islamabad"}
          {filters.sector && <span className="text-emerald-700"> · {filters.sector}</span>}
        </h1>
        <div className="flex gap-2 items-center">
          <div className="flex-1 flex items-center bg-white border border-slate-200 rounded-xl px-3 focus-within:border-emerald-500">
            <Search className="w-5 h-5 text-slate-400 mr-2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchResults()} placeholder="Search businesses…" className="border-0 shadow-none focus-visible:ring-0 h-11" data-testid="search-input" />
            {q && <button onClick={() => { setQ(""); setTimeout(fetchResults, 0); }}><X className="w-4 h-4 text-slate-400" /></button>}
          </div>
          <Button onClick={fetchResults} className="h-11 bg-emerald-800 hover:bg-emerald-900 hidden sm:inline-flex" data-testid="search-btn">Search</Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="h-11 lg:hidden flex-shrink-0" data-testid="mobile-filter-trigger"><Filter className="w-4 h-4" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white">
              <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
              <div className="mt-6"><FilterPanel filters={filters} setFilters={setFilters} /></div>
            </SheetContent>
          </Sheet>
        </div>
        <SectorPills value={filters.sector} onChange={(s) => setFilters({ ...filters, sector: s })} />
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 bg-white rounded-2xl border border-slate-200 p-5">
            <FilterPanel filters={filters} setFilters={setFilters} />
          </div>
        </aside>

        <div className="lg:col-span-3">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/3] bg-slate-100 rounded-xl animate-pulse" />)}
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center bg-white">
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>No businesses match your filters.</div>
              <p className="text-slate-500 mt-2">Try clearing filters or adding your business to the directory.</p>
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-500 mb-3">{results.length} result{results.length === 1 ? "" : "s"}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {results.map((b) => <BusinessCard key={b.business_id} b={b} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
